import { isFerramentaUtilizada } from '@services/ferramentaPersist.js'
import type { TopicoContent } from '@content/topicos-content.js'
import type { TopicoProgresso, DiarioEntry } from '@services/db.js'

export interface PassoStatLinha {
  id: string
  titulo: string
  valor: string
  pendente: boolean
}

export interface PassoEstatisticas {
  linhas: PassoStatLinha[]
  percentual: number
  pendentes: number
}

interface Input {
  content: TopicoContent | null
  progresso: TopicoProgresso | null
  checklistItems: { id: string; concluido: boolean }[]
  diario: DiarioEntry[]
  temFerramenta: boolean
  topicoId: string
}

import { GUIA_LABELS } from '@utils/guiaLabels.js'

export function computePassoEstatisticas(input: Input): PassoEstatisticas {
  const { content, progresso, checklistItems, diario, temFerramenta, topicoId } = input
  const linhas: PassoStatLinha[] = []
  let totalMetricas = 0
  let metricasOk = 0

  if (checklistItems.length > 0) {
    const feitos = checklistItems.filter((c) => c.concluido).length
    const total = checklistItems.length
    const pendentes = total - feitos
    totalMetricas += total
    metricasOk += feitos
    linhas.push({
      id: 'checklist',
      titulo: 'Procedimentos na obra',
      valor:
        pendentes === 0
          ? `${total} ${total === 1 ? 'concluído' : 'concluídos'}`
          : `${pendentes} de ${total} ${pendentes === 1 ? 'pendente' : 'pendentes'}`,
      pendente: pendentes > 0,
    })
  }

  const campos = content?.metadados ?? []
  if (campos.length > 0) {
    const meta = progresso?.metadados ?? {}
    const vazios = campos.filter((f) => !meta[f.campo]?.trim())
    const preenchidos = campos.length - vazios.length
    totalMetricas += campos.length
    metricasOk += preenchidos
    linhas.push({
      id: 'campos',
      titulo: 'Campos de registro',
      valor:
        vazios.length === 0
          ? `${campos.length} preenchidos`
          : `${vazios.length} ${vazios.length === 1 ? 'vazio' : 'vazios'} de ${campos.length}`,
      pendente: vazios.length > 0,
    })
  }

  if (temFerramenta) {
    const utilizada = isFerramentaUtilizada(topicoId)
    totalMetricas += 1
    if (utilizada) metricasOk += 1
    linhas.push({
      id: 'recurso',
      titulo: GUIA_LABELS.recursoEtapaTitulo,
      valor: utilizada ? GUIA_LABELS.recursoUtilizado : GUIA_LABELS.recursoNaoUtilizado,
      pendente: !utilizada,
    })
  }

  if (diario.length === 0) {
    linhas.push({
      id: 'diario',
      titulo: GUIA_LABELS.diarioDaEtapa,
      valor: 'Sem anotações',
      pendente: true,
    })
  } else {
    linhas.push({
      id: 'diario',
      titulo: GUIA_LABELS.diarioDaEtapa,
      valor: `${diario.length} ${diario.length === 1 ? 'anotação' : 'anotações'}`,
      pendente: false,
    })
  }

  totalMetricas += 1
  if (progresso?.concluido) metricasOk += 1
  linhas.push({
    id: 'conclusao',
    titulo: 'Conclusão',
    valor: progresso?.concluido ? 'Marcado concluído' : 'Ainda não concluído',
    pendente: !progresso?.concluido,
  })

  const percentual = totalMetricas > 0 ? Math.round((metricasOk / totalMetricas) * 100) : 0
  const pendentes = linhas.filter((l) => l.pendente).length

  return { linhas, percentual, pendentes }
}
